import { type Constraint, type ConstraintId } from '@/domain/constraints';
import { type Entity, type EntityId, type SceneDocument } from '@/domain/model';

/**
 * Reversible document deltas.
 *
 * A patch records, for each thing it touches, what it was and what it became. Inverting
 * one is a matter of swapping those two fields, which makes undo exact by construction
 * rather than by an inverse operation someone had to derive correctly for every edit.
 *
 * ## Why a patch and not the tool's actions
 *
 * The obvious design is to store the `EditAction`s a tool emitted and undo by applying
 * their opposites. It breaks the moment constraints exist: an edit runs Tier 1, then the
 * numeric solver, then Tier 1 again, so the document that results differs from the one
 * the actions describe. Undoing the actions alone would leave every point the *solver*
 * moved sitting where it was, and the drawing would drift a little further from its
 * original state with each undo.
 *
 * Diffing the document after the whole pipeline has run captures the solver's work too,
 * so undo restores exactly the state the user saw — which is the only definition of undo
 * that anybody can rely on.
 */

export interface EntityChange {
  readonly id: EntityId;
  /** Absent when the entity was created. */
  readonly before?: Entity | undefined;
  /** Absent when the entity was deleted. */
  readonly after?: Entity | undefined;
}

export interface ConstraintChange {
  readonly id: ConstraintId;
  readonly before?: Constraint | undefined;
  readonly after?: Constraint | undefined;
}

export interface DocumentPatch {
  readonly entities: readonly EntityChange[];
  readonly constraints: readonly ConstraintChange[];
}

export const EMPTY_PATCH: DocumentPatch = Object.freeze({
  entities: Object.freeze([]),
  constraints: Object.freeze([]),
});

export function isEmptyPatch(patch: DocumentPatch): boolean {
  return patch.entities.length === 0 && patch.constraints.length === 0;
}

/**
 * Builds a patch from two documents, looking only at `touched`.
 *
 * The candidate set is supplied rather than derived: a full diff of a five-thousand
 * entity document on every pointer-move during a drag would cost more than the geometry
 * it is recording. The caller already knows what it edited — the actions it applied, the
 * entities that cascaded away, and the free points the solver moved.
 */
export function diffDocuments(
  before: SceneDocument,
  after: SceneDocument,
  touched: Iterable<EntityId>
): DocumentPatch {
  const entities: EntityChange[] = [];
  const seen = new Set<EntityId>();

  for (const id of touched) {
    if (seen.has(id)) continue;
    seen.add(id);

    const previous = before.entities.get(id);
    const next = after.entities.get(id);
    if (previous === next) continue;

    entities.push({
      ...(previous === undefined ? {} : { before: previous }),
      ...(next === undefined ? {} : { after: next }),
      id,
    });
  }

  // Constraints are few and change rarely, so a full comparison is cheaper than
  // threading a second touched-set through every caller.
  const constraints: ConstraintChange[] = [];
  const constraintIds = new Set<ConstraintId>([
    ...before.constraints.keys(),
    ...after.constraints.keys(),
  ]);
  for (const id of constraintIds) {
    const previous = before.constraints.get(id);
    const next = after.constraints.get(id);
    if (previous === next) continue;
    constraints.push({
      ...(previous === undefined ? {} : { before: previous }),
      ...(next === undefined ? {} : { after: next }),
      id,
    });
  }

  return { entities, constraints };
}

/**
 * Applies a patch literally.
 *
 * Deliberately **not** through `removeEntity`: that cascades to dependents, which is
 * right when a user deletes something and catastrophically wrong here. The patch already
 * lists every entity the original deletion removed, and re-running the cascade during a
 * redo would take out entities the patch says nothing about.
 */
export function applyPatch(document: SceneDocument, patch: DocumentPatch): SceneDocument {
  if (isEmptyPatch(patch)) return document;

  const entities = new Map(document.entities);
  for (const change of patch.entities) {
    if (change.after === undefined) {
      entities.delete(change.id);
    } else {
      entities.set(change.id, change.after);
    }
  }

  const constraints = new Map(document.constraints);
  for (const change of patch.constraints) {
    if (change.after === undefined) {
      constraints.delete(change.id);
    } else {
      constraints.set(change.id, change.after);
    }
  }

  return { ...document, entities, constraints };
}

/** Swaps `before` and `after` throughout. */
export function invertPatch(patch: DocumentPatch): DocumentPatch {
  return {
    entities: patch.entities.map((change) => ({
      ...(change.after === undefined ? {} : { before: change.after }),
      ...(change.before === undefined ? {} : { after: change.before }),
      id: change.id,
    })),
    constraints: patch.constraints.map((change) => ({
      ...(change.after === undefined ? {} : { before: change.after }),
      ...(change.before === undefined ? {} : { after: change.before }),
      id: change.id,
    })),
  };
}

/**
 * Combines two consecutive patches into one.
 *
 * This is history compression: a drag emits a patch per pointer-move, and without
 * merging, a two-second gesture would leave four hundred undo steps that each move a
 * point by a pixel. Merging keeps the **oldest** `before` and the **newest** `after` for
 * each id, which is exactly the net effect of the whole gesture.
 *
 * An entity created by `earlier` and deleted by `later` collapses to nothing at all, and
 * is dropped rather than left as a no-op change.
 */
export function mergePatches(earlier: DocumentPatch, later: DocumentPatch): DocumentPatch {
  // Identity short-circuit, not just a micro-optimisation: a drag merges on every
  // pointer-move, and returning the same object lets the caller skip the work downstream
  // when nothing actually combined.
  if (isEmptyPatch(earlier)) return later;
  if (isEmptyPatch(later)) return earlier;

  return {
    entities: mergeChanges(earlier.entities, later.entities),
    constraints: mergeChanges(earlier.constraints, later.constraints),
  };
}

interface Change<K> {
  readonly id: K;
  readonly before?: unknown;
  readonly after?: unknown;
}

function mergeChanges<K, C extends Change<K>>(
  earlier: readonly C[],
  later: readonly C[]
): readonly C[] {
  if (earlier.length === 0) return later;
  if (later.length === 0) return earlier;

  const merged = new Map<K, C>();
  const order: K[] = [];

  for (const change of earlier) {
    merged.set(change.id, change);
    order.push(change.id);
  }

  for (const change of later) {
    const existing = merged.get(change.id);
    if (existing === undefined) {
      merged.set(change.id, change);
      order.push(change.id);
      continue;
    }
    // Oldest `before`, newest `after`.
    const combined = {
      ...existing,
      ...('after' in change ? { after: change.after } : {}),
      id: change.id,
    } as C;

    // `after` must be dropped, not set to undefined, when the later change deletes it.
    const next = 'after' in change ? combined : deleteAfter(existing);
    merged.set(change.id, next);
  }

  const result: C[] = [];
  for (const id of order) {
    const change = merged.get(id);
    if (change === undefined) continue;
    // Created then deleted within the same gesture: nothing happened.
    if (!('before' in change) && !('after' in change)) continue;
    result.push(change);
  }
  return result;
}

function deleteAfter<C extends object>(change: C): C {
  const copy = { ...change } as Record<string, unknown>;
  delete copy['after'];
  return copy as C;
}
